const text = value => (value == null ? '' : String(value).trim())
const unique = values => [...new Set(values.map(text).filter(Boolean))]
export const inPlanningScope = (row, siteId, assetId) =>
  (!siteId || row.siteId === String(siteId)) &&
  (!assetId || row.assetId === String(assetId))
export const planningScopeKey = (companyId, siteId, assetId) =>
  JSON.stringify([
    String(companyId),
    String(siteId || ''),
    String(assetId || '')
  ])

// One proposed item per draft/component. Techniques remain requirements on that
// item. No occurrence IDs, completion states, reports or audit events are invented.
export function buildPlanningItems (drafts, companyId, catalog = null) {
  const current =
    catalog === null ? null : new Map(catalog.map(row => [row.key, row]))
  const draftIds = new Set()
  const result = []
  for (const draft of drafts) {
    if (text(draft.companyId) !== text(companyId)) continue
    if (!text(draft.id) || !draft.details || !Array.isArray(draft.components))
      throw new Error('A local draft has an invalid structure.')
    if (draftIds.has(draft.id))
      throw new Error('Local draft IDs must be unique.')
    draftIds.add(draft.id)
    const keys = new Set()
    for (const component of draft.components) {
      if (!text(component.key) || keys.has(component.key))
        throw new Error(
          'A draft has missing or duplicate component identities.'
        )
      keys.add(component.key)
      const available = current?.get(component.key)
      const techniques = unique(component.techniques || [])
      result.push({
        ...component,
        id: JSON.stringify([draft.id, component.key]),
        draftId: draft.id,
        schedule: text(draft.details.name),
        owner: text(draft.details.owner),
        due: text(draft.details.due),
        frequency: draft.details.frequency,
        notes: text(draft.details.notes),
        savedAt: draft.savedAt,
        techniques,
        snapshot: draft,
        catalogueState:
          current === null
            ? 'Not checked'
            : available
            ? 'Available'
            : 'Unavailable',
        unconfirmed: available
          ? techniques.filter(method => !available.eligible.includes(method))
          : []
      })
    }
  }
  return result
}

export function buildPlanningCoverage (catalog, items) {
  const byComponent = new Map()
  items.forEach(item => {
    const rows = byComponent.get(item.key) || []
    rows.push(item)
    byComponent.set(item.key, rows)
  })
  return catalog.map(component => {
    const assignments = byComponent.get(component.key) || []
    const draftCount = new Set(assignments.map(item => item.draftId)).size
    return {
      ...component,
      id: component.key,
      assignments,
      draftCount,
      techniques: unique(assignments.flatMap(item => item.techniques)),
      planState:
        draftCount === 0
          ? 'Not in local drafts'
          : draftCount === 1
          ? 'One local draft'
          : 'Multiple local drafts'
    }
  })
}
export function planningSummary (rows) {
  return {
    total: rows.length,
    included: rows.filter(row => row.draftCount > 0).length,
    unplanned: rows.filter(row => row.draftCount === 0).length,
    overlap: rows.filter(row => row.draftCount > 1).length
  }
}
export const planningFilters = [
  {
    key: 'subsystem',
    label: 'Subsystem',
    values: row => [row.subsystem || 'Not recorded']
  },
  {
    key: 'technique',
    label: 'Assigned technique',
    values: row => row.techniques
  }
]
export const coverageFilters = [
  ...planningFilters,
  { key: 'planState', label: 'Draft inclusion', values: row => [row.planState] }
]
export const planningSearch = row =>
  [
    row.code,
    row.name,
    row.siteLabel,
    row.assetLabel,
    row.siteId,
    row.assetId,
    row.subsystem,
    row.schedule,
    row.owner,
    ...row.techniques
  ]
    .filter(Boolean)
    .join(' ')

const componentColumn = {
  key: 'code',
  label: 'Component',
  value: row => row.code
}
const locationColumn = {
  key: 'location',
  label: 'Location',
  value: row => row.siteLabel + ' / ' + row.assetLabel
}
const techniqueColumn = {
  key: 'techniques',
  label: 'Assigned techniques',
  value: row => row.techniques.join(' · ')
}
export const itemColumns = [
  componentColumn,
  locationColumn,
  { key: 'schedule', label: 'Local draft', value: row => row.schedule },
  techniqueColumn,
  { key: 'due', label: 'Proposed first due', value: row => row.due },
  {
    key: 'catalogueState',
    label: 'Catalogue link',
    value: row => row.catalogueState
  }
]
export const coverageColumns = [
  componentColumn,
  locationColumn,
  { key: 'subsystem', label: 'Subsystem', value: row => row.subsystem },
  {
    key: 'draftCount',
    label: 'Local drafts',
    value: row => row.draftCount,
    numeric: true
  },
  techniqueColumn,
  { key: 'planState', label: 'Draft inclusion', value: row => row.planState }
]
export function sortPlanning (rows, sort, columns) {
  const column = columns.find(item => item.key === sort.key) || columns[0]
  return [...rows].sort((a, b) => {
    const left = column.value(a),
      right = column.value(b)
    const difference = column.numeric
      ? left - right
      : text(left).localeCompare(text(right), undefined, { numeric: true })
    return (
      (sort.direction === 'desc' ? -1 : 1) * difference ||
      a.id.localeCompare(b.id)
    )
  })
}
export function previewItemsQuery (
  previous,
  { draftId = null, componentKey = null } = {}
) {
  const next = new URLSearchParams(previous)
  next.set('inspectionView', 'items')
  next.delete('tab')
  for (const [key, value] of [
    ['plan.draft', draftId],
    ['plan.component', componentKey]
  ]) {
    if (value) next.set(key, value)
    else next.delete(key)
  }
  for (const key of [...next.keys()])
    if (key.startsWith('planItems.')) next.delete(key)
  next.delete('plan.item')
  next.delete('plan.scope')
  return next
}
